import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { Dialog, Grid, TextField } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { ProductDetail } from 'commons/types';
import {
  getBinProductDetail,
  updateBinInventoryNumber,
} from 'redux-stores/actions';
import { AppDispatch } from 'redux-stores/store';

import S from '../styles/EditBinDialog.styles';
import { IEditBinModalProps } from '../types';

export const EditBinInventoryNumberDialog: React.FC<IEditBinModalProps> = ({
  onClose,
  openModal,
  location,
  binData,
}) => {
  const [bin, setBin] = useState<Partial<ProductDetail>>(binData);
  const [binError, setBinError] = useState<string | null>(null);
  const [newInventoryNumber, setNewInventoryNumber] = useState<string>('');
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    setBin(binData);
  }, [binData]);

  const onConfirm = async (): Promise<void> => {
    if (
      bin &&
      bin.bin &&
      bin.sku_code &&
      bin.bin_type &&
      bin.inventory_number &&
      newInventoryNumber
    ) {
      dispatch(
        updateBinInventoryNumber({
          bin: {
            bin: bin.bin,
            bin_type: bin.bin_type,
            sku_code: bin.sku_code,
            inventory_number: bin.inventory_number,
          },
          newInventoryNumber,
          location,
        }),
      );
    }
  };

  const debouncedProductDetail = useDebouncedCallback(
    async (params: {
      binNumber: string;
      skuCode: string;
      inventoryNumber: string;
      binState: Partial<ProductDetail>;
    }) => {
      const { binState, binNumber, skuCode, inventoryNumber } = params;
      const result = await getBinProductDetail(
        {
          location,
          bin: binNumber,
          sku_code: skuCode,
          inventory_number: inventoryNumber,
        },
        dispatch,
      );

      const clonedBin = structuredClone(binState);
      if (result) {
        clonedBin.inventory_number = result.inventory_number;
        setBinError(null);
      } else {
        clonedBin.inventory_number = '';
        setBinError('Inventory number not found');
      }
      setBin(clonedBin);
    },
    500,
    { maxWait: 2000, trailing: true },
  );

  const onFieldChange = (
    _type: 'inventory_number' | 'new_inventory_number',
    value: string | null,
  ): void => {
    let newBin = structuredClone(bin);

    if (_type === 'inventory_number') {
      newBin = { ...newBin, [_type]: value || '' };
      setBin(newBin);
    } else if (_type === 'new_inventory_number') {
      setNewInventoryNumber(value || '');
    }

    const {
      bin: binNumber,
      sku_code: skuCode,
      inventory_number: inventoryNumber,
    } = newBin;

    if (
      _type === 'inventory_number' &&
      binNumber &&
      skuCode &&
      inventoryNumber
    ) {
      debouncedProductDetail({
        binNumber,
        skuCode,
        inventoryNumber,
        binState: bin,
      });
    }
  };

  const isConfirmDisabled = (): boolean => {
    return !(
      newInventoryNumber &&
      newInventoryNumber.trim() !== '' &&
      bin.inventory_number &&
      bin.inventory_number.trim() !== ''
    );
  };

  return (
    <Dialog
      onClose={onClose}
      open={openModal}
      style={{ whiteSpace: 'pre-line' }}
    >
      <S.TitleWrapper>
        <S.Title>Edit Bin Inventory Number</S.Title>
      </S.TitleWrapper>
      <Grid container style={{ marginTop: '12px', width: '500px' }}>
        <S.FieldRowWrapper container item xs={12} spacing={2}>
          <Grid item xs={5}>
            <S.Text>Bin</S.Text>
          </Grid>
          <S.InputWrapper item xs={7}>
            <S.Text>{bin.bin}</S.Text>
          </S.InputWrapper>
        </S.FieldRowWrapper>
        <S.FieldRowWrapper container item xs={12} spacing={2}>
          <Grid item xs={5}>
            <S.Text>Inventory Number</S.Text>
          </Grid>
          <S.InputWrapper item xs={7}>
            <TextField
              placeholder="Inventory Number"
              variant="outlined"
              value={bin.inventory_number || ''}
              error={!!binError}
              helperText={binError}
              onChange={(event): void =>
                onFieldChange('inventory_number', event.target.value)
              }
              fullWidth
            />
          </S.InputWrapper>
        </S.FieldRowWrapper>
        <S.FieldRowWrapper container item xs={12} spacing={2}>
          <Grid item xs={5}>
            <S.Text>New Inventory Number</S.Text>
          </Grid>
          <S.InputWrapper item xs={7}>
            <TextField
              placeholder="New Inventory Number"
              variant="outlined"
              value={newInventoryNumber || ''}
              onChange={(event): void =>
                onFieldChange('new_inventory_number', event.target.value)
              }
              fullWidth
            />
          </S.InputWrapper>
        </S.FieldRowWrapper>
      </Grid>
      <S.FooterWrapper>
        <S.SecondaryButton onClick={onClose}>Cancel</S.SecondaryButton>
        <S.PrimaryButton
          onClick={() => {
            onConfirm();
            onClose();
          }}
          disabled={isConfirmDisabled()}
        >
          Update
        </S.PrimaryButton>
      </S.FooterWrapper>
    </Dialog>
  );
};
